import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Form, InputField } from "@mimber/forms"
import { Button, Typography, message } from "@mimber/ui-web"
import { recoveryCodeLoginSchema } from "@mimber/auth"
import { useVerifyRecoveryCode } from "@mimber/client/mimber-organization"
import type {
  RecoveryCodeLoginFormProps,
  RecoveryCodeLoginFormData,
} from "./RecoveryCodeLoginForm.types"

const { Paragraph, Title } = Typography

export const RecoveryCodeLoginForm: React.FunctionComponent<
  RecoveryCodeLoginFormProps
> = ({ email, onSuccess, onCancel }) => {
  const verifyMutation = useVerifyRecoveryCode({
    onSuccess: () => {
      message.success("MFA has been reset. Please sign in again.")
      onSuccess()
    },
    onError: (error: Error) => {
      message.error(error.message || "Invalid recovery code. Please try again.")
    },
  })

  const form = useForm<RecoveryCodeLoginFormData>({
    resolver: zodResolver(recoveryCodeLoginSchema),
    defaultValues: {
      code: "",
    },
  })

  const handleSubmit = (data: RecoveryCodeLoginFormData) => {
    verifyMutation.mutate({ email, code: data.code })
  }

  return (
    <>
      <div className="mb-6 text-center">
        <Title level={4}>Use a recovery code</Title>
        <Paragraph>
          Enter one of your recovery codes to reset MFA and sign in.
        </Paragraph>
      </div>

      <Form form={form} onSubmit={handleSubmit}>
        <InputField
          name="code"
          type="text"
          label="Recovery Code"
          placeholder="Enter 8-character code"
          maxLength={8}
          required
        />
        <Button
          type="primary"
          htmlType="submit"
          loading={verifyMutation.isPending}
          className="w-full mb-3"
          data-testid="recovery-code-submit-button"
        >
          Verify Recovery Code
        </Button>
      </Form>

      <div className="flex justify-start">
        <Button
          type="link"
          onClick={onCancel}
          data-testid="recovery-code-cancel-button"
        >
          Back
        </Button>
      </div>
    </>
  )
}
